import {
  AreaOccupancyData,
  SchedulerAreaData,
  SchedulerConfigOccupancyData,
  SchedulerConfigurationData,
  UnavailabilityData,
} from '../../../api';
import { isWholeAreaBlock, maskToIndexes } from '../../../utils/maskUtils';

/** Whether a block is in force at a given moment. All-day blocks always are. */
export const blockCoversTime = (unavailability: UnavailabilityData, seconds: number): boolean =>
  unavailability.all_day || (unavailability.start_time <= seconds && seconds < unavailability.end_time);

/** Blocks recorded against this area on the day being viewed. */
export const blocksForArea = (
  unavailabilities: UnavailabilityData[],
  occupancyConfig: SchedulerConfigOccupancyData,
  area: AreaOccupancyData,
): UnavailabilityData[] =>
  unavailabilities.filter((unavailability) =>
    (unavailability.shared_resources_blocks?.scheduler_configs ?? []).some(
      (block) => block.scheduler_config_id === occupancyConfig.scheduler_config_id && block.area_id === area.area_id,
    ),
  );

/**
 * Whether the one-click "open area" may delete this block — true only for what the close-area
 * toggle itself creates: a whole-area, non-repeating block covering only that area. A close-place block
 * spans every area, so deleting it from one area's chip would reopen the whole place.
 *
 * Every unavailability row covers exactly one date (the backend writes one row per day of a range
 * and only links rows for repeating blocks), so deleting a row can never reopen another day.
 * Partial and repeating blocks still go to the unavailability drawer, where their shape is visible.
 */
export const isSimpleAreaBlock = (unavailability: UnavailabilityData, area: AreaOccupancyData): boolean => {
  if (unavailability.recurrent_unavailability_id) {
    return false;
  }
  const schedulerBlocks = unavailability.shared_resources_blocks?.scheduler_configs ?? [];
  return (
    schedulerBlocks.length === 1 &&
    schedulerBlocks[0].area_id === area.area_id &&
    isWholeAreaBlock(schedulerBlocks[0].blocked_mask, area.mask)
  );
};

/** The areas occupancy reports for a config: its own, or one implicit whole-config area (same rule as the backend). */
export const configAreaDefinitions = (config: SchedulerConfigurationData): SchedulerAreaData[] =>
  config.areas?.length
    ? config.areas
    : [{ id: config.id, label: config.label, mask: '1'.repeat(config.resource_count) }];

/**
 * A block that closes the whole place: for every scheduler config, the union of its blocked masks
 * covers every resource of every area. Matched by mask rather than area_id so rows made in the
 * unavailability drawer, which writes no area_id, count too.
 */
export const isClosePlaceBlock = (
  unavailability: UnavailabilityData,
  configs: SchedulerConfigurationData[],
): boolean => {
  if (unavailability.recurrent_unavailability_id || configs.length === 0) {
    return false;
  }
  const schedulerBlocks = unavailability.shared_resources_blocks?.scheduler_configs ?? [];
  return configs.every((config) => {
    const blockedIndexes = new Set(
      schedulerBlocks
        .filter((block) => block.scheduler_config_id === config.id)
        .flatMap((block) => maskToIndexes(block.blocked_mask)),
    );
    const areaIndexes = configAreaDefinitions(config).flatMap((area) => maskToIndexes(area.mask));
    return areaIndexes.every((index) => blockedIndexes.has(index));
  });
};
